import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, Phone, X, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSettings } from "@/context/SettingsContext";

const links = [
  { href: "#services", label: "Services" },
  { href: "#about", label: "About" },
  { href: "#area", label: "Service Area" },
  { href: "#testimonials", label: "Reviews" },
  { href: "#contact", label: "Contact" },
];

export default function Header() {
  const { settings } = useSettings();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const phone = settings?.phone || "240-383-9887";
  const company = settings?.company_name || "B&J Electric Service LLC";

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-black/70 backdrop-blur-xl border-b border-white/10"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8 h-20 flex items-center justify-between">
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2.5 group">
          <div className="w-10 h-10 rounded-md bg-yellow-400 flex items-center justify-center shadow-[0_0_20px_rgba(250,204,21,0.35)] group-hover:scale-105 transition-transform">
            <Zap className="w-5 h-5 text-black" strokeWidth={2.5} />
          </div>
          <div className="hidden sm:block">
            <div className="text-white font-display font-bold tracking-tight leading-none">{company.split(" ").slice(0, 2).join(" ")}</div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-white/50 mt-0.5">Electric Service</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.label.toLowerCase().replace(" ", "-")}`}
              className="text-sm text-white/70 hover:text-yellow-400 transition-colors relative group"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-yellow-400 group-hover:w-full transition-all duration-300" />
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={`tel:${phone.replace(/[^0-9]/g, "")}`}
            data-testid="header-call-button"
            className="hidden sm:inline-flex items-center gap-2 bg-yellow-400 hover:bg-yellow-500 text-black font-bold px-4 py-2.5 rounded-md text-sm transition-all hover:scale-[1.03] active:scale-95"
          >
            <Phone className="w-4 h-4" strokeWidth={2.5} />
            <span className="hidden md:inline">{phone}</span>
          </a>
          <a href="#quote" className="hidden md:inline-block">
            <Button
              data-testid="header-quote-button"
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md"
            >
              Get a Quote
            </Button>
          </a>
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden text-white p-2"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-black/95 backdrop-blur-xl border-t border-white/10">
          <nav className="px-5 py-6 flex flex-col gap-4">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`mobile-nav-${l.label.toLowerCase().replace(" ", "-")}`}
                onClick={() => setOpen(false)}
                className="text-white/80 hover:text-yellow-400 py-1.5"
              >
                {l.label}
              </a>
            ))}
            <a href="#quote" onClick={() => setOpen(false)}>
              <Button className="w-full bg-yellow-400 hover:bg-yellow-500 text-black font-bold" data-testid="mobile-quote-button">
                Get a Free Quote
              </Button>
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}
